import React, { useState } from 'react';
import { mapMarkerImageUrl } from '../utils/cdnImageUrl';
import './VenueBrandingPreview.css';

export type BrandingThumbSize = 'sm' | 'md' | 'lg';

const HEIGHT_BY_SIZE: Record<BrandingThumbSize, number> = {
  sm: 22,
  md: 30,
  lg: 44,
};

type VenueBrandingThumbProps = {
  storedValue: string | undefined | null;
  alt: string;
  size?: BrandingThumbSize;
  title?: string;
};

export const VenueBrandingThumb: React.FC<VenueBrandingThumbProps> = ({
  storedValue,
  alt,
  size = 'sm',
  title,
}) => {
  const [failed, setFailed] = useState(false);
  const src = mapMarkerImageUrl(storedValue);
  const height = HEIGHT_BY_SIZE[size];

  if (!src || failed) {
    return null;
  }

  return (
    <img
      className="venue-branding-thumb"
      src={src}
      alt={alt}
      title={title ?? storedValue ?? undefined}
      style={{ height, width: 'auto', maxWidth: height * 2 }}
      onError={() => setFailed(true)}
    />
  );
};

type VenueBrandingPairProps = {
  mapMarker?: string | null;
  venueIcon?: string | null;
  size?: BrandingThumbSize;
};

export const VenueBrandingPair: React.FC<VenueBrandingPairProps> = ({
  mapMarker,
  venueIcon,
  size = 'sm',
}) => {
  const hasMap = Boolean(String(mapMarker ?? '').trim());
  const hasIcon = Boolean(String(venueIcon ?? '').trim());
  if (!hasMap && !hasIcon) return null;

  return (
    <span className="venue-branding-pair" aria-hidden={!hasMap && !hasIcon}>
      {hasMap && (
        <VenueBrandingThumb storedValue={mapMarker} alt="Map pin" size={size} title={`Map: ${mapMarker}`} />
      )}
      {hasIcon && (
        <VenueBrandingThumb
          storedValue={venueIcon}
          alt="List/calendar icon"
          size={size}
          title={`Icon: ${venueIcon}`}
        />
      )}
    </span>
  );
};

type VenueNameWithBrandingProps = {
  name: string;
  mapMarker?: string | null;
  venueIcon?: string | null;
  subtitle?: string;
};

export const VenueNameWithBranding: React.FC<VenueNameWithBrandingProps> = ({
  name,
  mapMarker,
  venueIcon,
  subtitle,
}) => (
  <div className="venue-name-with-branding">
    <VenueBrandingPair mapMarker={mapMarker} venueIcon={venueIcon} size="md" />
    <div>
      <h3 className="venue-name-with-branding__title">{name || 'Untitled'}</h3>
      {subtitle ? (
        <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>{subtitle}</div>
      ) : null}
    </div>
  </div>
);

type BrandingSelectPreviewProps = {
  label: string;
  value: string | undefined | null;
  children: React.ReactNode;
};

export const BrandingSelectPreview: React.FC<BrandingSelectPreviewProps> = ({
  label,
  value,
  children,
}) => (
  <div className="form-group">
    <label>{label}</label>
    <div className="branding-select-preview">
      <div className="branding-select-preview__select">{children}</div>
      <VenueBrandingThumb storedValue={value} alt={label} size="lg" />
      {value?.trim() ? (
        <span className="branding-select-preview__caption" title={value}>
          {value}
        </span>
      ) : null}
    </div>
  </div>
);
